import { useEffect } from 'react'
import { Alert, Form, Input, Modal, Select, Tag, Typography } from 'antd'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
// ⚠️ Naqsh ABONENT modulidan olinadi, bu yerda qayta yozilmaydi
// (`LeadsPage` dagi yaratish formasi bilan AYNAN bitta manba).
import { PHONE_E164, normalizePhone } from '@/features/subscriber/subscriberApi'
import {
  leadEditBody,
  leadEditPlan,
  updateLead,
  LEAD_SOURCES,
  type Lead,
  type LeadEditForm,
} from './crmApi'

/**
 * Lid tavsifini to'g'rilash — `PUT /api/crm/leads/{id}`.
 *
 * <h2>⚠️ SERVER TO'LIQ ALMASHTIRADI</h2>
 * `LeadResource#update` YARATISH bilan bir xil DTO ni oladi va
 * `LeadRepository.updateDetails` oltala ustunni so'zsiz qayta yozadi.
 * Ya'ni so'rovda tushib qolgan maydon bazada JIMGINA tozalanadi.
 * Shuning uchun tana bu yerda YIG'ILMAYDI — u `leadEditBody` da
 * quriladi va qorovul uni CHAQIRIB o'lchaydi.
 *
 * <h2>⚠️ HOLAT KO'RINADI, LEKIN TAHRIRLANMAYDI</h2>
 * Lid holati o'z amallari bilan o'zgaradi (`contacted`, `qualify`,
 * `lost`, `convert/*`) va ular holat mashinasiga bo'ysunadi
 * (`LEAD_NEXT`, bazada `lead_state_transition_guard`). Serverdagi
 * `LeadDraft` da holat maydoni umuman yo'q — ya'ni formadan yuborilgan
 * holat qabul qilinmasdi va operator buni bilmasdi. Ekranda holat
 * ko'rinadi, chunki u tahrirning KONTEKSTI: yopilgan lid umuman
 * tahrirlanmaydi.
 *
 * <h2>⚠️ MAS'UL BU YERDA O'ZGARMAYDI, LEKIN YUBORILADI</h2>
 * `assigned_to` ham to'liq almashtiriladigan ustun. Formada u yo'q
 * (biriktirish — alohida amal, `POST /{id}/assign`), shuning uchun
 * `leadEditBody` uni JORIY qiymatidan qaytarib yuboradi.
 *
 * <h2>⚠️ TELEFON — OCHILGAN BO'LISHI SHART</h2>
 * Ro'yxat niqoblangan raqam beradi, `PUT` esa to'liq raqam kutadi.
 * Sabab va uchta yo'lning nega ishlamasligi — `leadEditPlan` javadoc'ida.
 */
interface LeadEditModalProps {
  /** `null` — oyna yopiq */
  lead: Lead | null
  /** `revealLeadPhone` bergan TO'LIQ raqam; `undefined` — ochilmagan */
  fullPhone: string | undefined
  onCancel: () => void
  /** @param phone saqlangan to'liq raqam — sahifadagi ochilganlar xaritasi uchun */
  onDone: (phone: string | null) => void
}

export function LeadEditModal({ lead, fullPhone, onCancel, onDone }: LeadEditModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<LeadEditForm>()

  const plan = lead === null ? null : leadEditPlan(lead, fullPhone)

  /*
    ⚠️ Boshlang'ich qiymatlar `leadEditPlan` DAN keladi, `lead` dan
    to'g'ridan-to'g'ri emas: xom `string` ni sanoqli tipga keltirish
    aynan o'sha funksiyada, bir marta va zaxira qiymatsiz bajariladi.
  */
  useEffect(() => {
    if (lead === null) return
    const next = leadEditPlan(lead, fullPhone)
    if (next.block === null) form.setFieldsValue(next.values)
  }, [lead, fullPhone, form])

  const save = useMutation({
    mutationFn: (values: LeadEditForm) => updateLead(lead!.id, leadEditBody(values, lead!)),
    onSuccess: (_updated, values) => onDone(normalizePhone(values.phone)),
    // ⚠️ `onError` ATAYLAB yo'q — xato OYNANING ICHIDA chiziladi.
  })

  const dismiss = () => {
    save.reset()
    onCancel()
  }

  return (
    <Modal
      open={lead !== null && plan !== null && plan.block === null}
      title={t('crm.leadEdit.title')}
      onCancel={dismiss}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
    >
      {lead ? (
        <Typography.Paragraph type="secondary">
          {t('crm.leadEdit.subtitle')}{' '}
          <Tag>{t(`crm.leadStatus.${lead.status}`)}</Tag>
        </Typography.Paragraph>
      ) : null}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('crm.leadEdit.statusLocked')}
        description={t('crm.leadEdit.statusLockedHint')}
      />

      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<LeadEditForm> form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="fullName"
          label={t('crm.lead.fullName')}
          rules={[{ required: true, message: t('crm.lead.fullNameRequired') }]}
        >
          <Input autoFocus />
        </Form.Item>
        {/*
          ⚠️ Maydon TO'LIQ raqam bilan to'ladi (ochilgan), niqob bilan
          emas — niqob serverda 400 berardi.

          ⚠️ Raqam MAJBURIY EMAS: serverdagi `LeadDraft.phone` da
          `@NotNull` yo'q va telefonsiz lid (faqat tashkilot nomi bilan)
          haqiqiy holat. Lekin maydonni BO'SHATISH — raqamni O'CHIRISH,
          shuning uchun bu ogohlantirish maydon yonida turadi.

          ⚠️ Shakl tekshiruvi MAMLAKATGA BOG'LANMAGAN: `+998` ham,
          `+93` ham o'tadi (egasi qarori — o'rnatma Afg'onistonda ham
          ishlaydi). Naqsh serverdagi `Phones.E164` ning aynan o'zi.
        */}
        <Form.Item
          name="phone"
          label={t('crm.lead.phone')}
          extra={t('crm.leadEdit.phoneHint')}
          rules={[
            {
              validator: (_, value: string | undefined) => {
                const clean = normalizePhone(value)
                if (clean === null || PHONE_E164.test(clean)) return Promise.resolve()
                return Promise.reject(new Error(t('crm.lead.phoneFormat')))
              },
            },
          ]}
        >
          <Input inputMode="tel" autoComplete="off" />
        </Form.Item>
        <Form.Item name="companyName" label={t('crm.lead.company')}>
          <Input />
        </Form.Item>
        <Form.Item
          name="source"
          label={t('crm.lead.source')}
          rules={[{ required: true }]}
        >
          <Select
            options={LEAD_SOURCES.map((s) => ({ value: s, label: t(`crm.leadSource.${s}`) }))}
          />
        </Form.Item>
        <Form.Item name="notes" label={t('crm.lead.notes')}>
          <Input.TextArea rows={3} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
